import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { UsageEventsApi } from '../../services/ApiService';
import { LoadingChart, EmptyState } from '../../components/analytics/SharedAnalyticsComponents';

const EVENT_TYPE_COLORS = {
  PAGE_VIEW: '#007bff',
  NAV_CLICK: '#28a745',
  DOCUMENT_VIEW: '#ffc107',
  DOCUMENT_DOWNLOAD: '#dc3545',
  DOCUMENT_OPEN_NEW_TAB: '#fd7e14',
  COURSE_SELECT: '#6f42c1',
  ASSIGNMENT_COMPLETE: '#17a2b8',
  ASSIGNMENT_UNCOMPLETE: '#6c757d',
  TAB_CHANGE: '#20c997',
  LOGIN_SUCCESS: '#28a745',
  LOGIN_ATTEMPT: '#ffc107',
  THEME_CHANGE: '#e83e8c',
  PERFORMANCE_PAGE_LOAD: '#17a2b8',
  PERFORMANCE_API_CALL: '#dc3545'
};

/**
 * SessionJourneyVisualization
 * 
 * Displays a chronological vertical timeline of all events within a specific session.
 * Shows page views, clicks, and other interactions to understand user navigation flow.
 * 
 * Features:
 * - Vertical timeline with event cards
 * - Journey metrics (duration, event count, unique pages)
 * - Event type filtering (PAGE_VIEW, NAV_CLICK, etc.)
 * - Time-on-page calculations
 * - Handles anonymous sessions (userId=null)
 */
export default function SessionJourneyVisualization() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [sessionInfo, setSessionInfo] = useState(null);
  
  // Event type filters (all enabled by default)
  const [enabledTypes, setEnabledTypes] = useState({
    PAGE_VIEW: true,
    NAV_CLICK: true,
    DOCUMENT_VIEW: true,
    DOCUMENT_DOWNLOAD: true,
    DOCUMENT_OPEN_NEW_TAB: true,
    COURSE_SELECT: true,
    ASSIGNMENT_COMPLETE: true,
    ASSIGNMENT_UNCOMPLETE: true,
    TAB_CHANGE: true,
    LOGIN_SUCCESS: true,
    LOGIN_ATTEMPT: true,
    THEME_CHANGE: true,
    PERFORMANCE_PAGE_LOAD: true,
    PERFORMANCE_API_CALL: true
  });

  // Load session events
  useEffect(() => {
    if (!sessionId) return;
    
    const loadSessionData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Use admin search to support anonymous sessions (no userId filter)
        const response = await UsageEventsApi.adminSearch({
          sessionId,
          from: new Date(Date.now() - 30 * 86400000).toISOString(), // Last 30 days
          to: new Date().toISOString(),
          page: 0,
          size: 500 // Sufficient for typical session
        });
        
        if (response?.ok) {
          const data = await response.json();
          const sortedEvents = (data.content || []).sort((a, b) => 
            new Date(a.occurredAt) - new Date(b.occurredAt)
          );
          
          setEvents(sortedEvents);
          
          // Extract session info from first event
          if (sortedEvents.length > 0) {
            const firstEvent = sortedEvents[0];
            const lastEvent = sortedEvents[sortedEvents.length - 1];
            setSessionInfo({
              userId: firstEvent.userId,
              sessionId: firstEvent.sessionId,
              firstEventAt: firstEvent.occurredAt,
              lastEventAt: lastEvent.occurredAt,
              eventCount: sortedEvents.length
            });
          }
        } else {
          throw new Error(`Failed to load session data: ${response?.status || 'unknown'}`);
        }
      } catch (err) {
        console.error('Error loading session journey:', err);
        setError(err.message || 'Failed to load session data');
      } finally {
        setLoading(false);
      }
    };
    
    loadSessionData();
  }, [sessionId]);

  // Calculate journey metrics
  const metrics = useMemo(() => {
    if (events.length === 0) return null;
    
    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    const duration = new Date(lastEvent.occurredAt) - new Date(firstEvent.occurredAt);
    
    // Count unique pages (PAGE_VIEW events with distinct entityId)
    const pageViews = events.filter(e => e.eventType === 'PAGE_VIEW');
    const uniquePages = new Set(
      pageViews.map(e => {
        try {
          const payload = JSON.parse(e.payloadJson);
          return payload.entityId || payload.label || 'Unknown';
        } catch {
          return 'Unknown';
        }
      })
    ).size;
    
    // Calculate average time per page
    const avgTimePerPage = pageViews.length > 1 
      ? duration / (pageViews.length - 1) 
      : duration;
    
    return {
      duration,
      durationFormatted: formatDuration(duration),
      eventCount: events.length,
      uniquePages,
      avgTimePerPage,
      avgTimePerPageFormatted: formatDuration(avgTimePerPage)
    };
  }, [events]);

  // Filter events by enabled types
  const filteredEvents = useMemo(() => {
    return events.filter(event => enabledTypes[event.eventType] !== false);
  }, [events, enabledTypes]);

  // Toggle event type filter
  const toggleEventType = (eventType) => {
    setEnabledTypes(prev => ({
      ...prev,
      [eventType]: !prev[eventType]
    }));
  };

  // Get unique event types in this session
  const sessionEventTypes = useMemo(() => {
    const types = new Set(events.map(e => e.eventType));
    return Array.from(types).sort();
  }, [events]);

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <LoadingChart />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ background: 'var(--card-bg)', padding: '2rem', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
          <h2 style={{ marginTop: 0, color: 'var(--danger)' }}>Error Loading Session</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/settings/usage-events')}
            style={{ padding: '0.5rem 1rem', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div style={{ padding: '2rem' }}>
        <EmptyState message="No events found for this session" />
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button 
            onClick={() => navigate('/settings/usage-events')}
            style={{ padding: '0.5rem 1rem', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', minHeight: '100vh', overflow: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <button 
            onClick={() => navigate('/settings/usage-events')}
            style={{ padding: '0.5rem 1rem', background: 'var(--surface-secondary)', border: '1px solid var(--border-light)', borderRadius: '6px', cursor: 'pointer' }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{ margin: 0 }}>Session Journey</h1>
        </div>
        
        {sessionInfo && (
          <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', fontSize: '0.9rem' }}>
              <div>
                <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Session ID:</span>
                <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', marginTop: '0.25rem' }}>{sessionId}</div>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>User:</span>
                <div style={{ marginTop: '0.25rem' }}>
                  {sessionInfo.userId ? (
                    <Link to={`/settings/usage-events?userId=${sessionInfo.userId}`} style={{ color: 'var(--primary)' }}>
                      User {sessionInfo.userId}
                    </Link>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Anonymous</span>
                  )}
                </div>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Started:</span>
                <div style={{ marginTop: '0.25rem' }}>{new Date(sessionInfo.firstEventAt).toLocaleString()}</div>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Ended:</span>
                <div style={{ marginTop: '0.25rem' }}>{new Date(sessionInfo.lastEventAt).toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Journey Metrics */}
      {metrics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <MetricCard label="Session Duration" value={metrics.durationFormatted} />
          <MetricCard label="Total Events" value={metrics.eventCount} />
          <MetricCard label="Unique Pages" value={metrics.uniquePages} />
          <MetricCard label="Avg Time/Page" value={metrics.avgTimePerPageFormatted} />
        </div>
      )}

      {/* Event Type Filters */}
      <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-light)', marginBottom: '2rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Filter Event Types</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {sessionEventTypes.map(eventType => (
            <label 
              key={eventType}
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                padding: '0.5rem 1rem',
                background: enabledTypes[eventType] ? EVENT_TYPE_COLORS[eventType] || 'var(--primary)' : 'var(--surface-secondary)',
                color: enabledTypes[eventType] ? '#fff' : 'var(--text-primary)',
                borderRadius: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: `1px solid ${enabledTypes[eventType] ? 'transparent' : 'var(--border-light)'}`,
                fontSize: '0.85rem',
                fontWeight: 500
              }}
            >
              <input 
                type="checkbox" 
                checked={enabledTypes[eventType] !== false}
                onChange={() => toggleEventType(eventType)}
                style={{ cursor: 'pointer' }}
              />
              {eventType}
            </label>
          ))}
        </div>
        <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Showing {filteredEvents.length} of {events.length} events
        </div>
      </div>

      {/* Vertical Timeline */}
      <div style={{ background: 'var(--card-bg)', padding: '2rem', borderRadius: '12px', border: '1px solid var(--border-light)', overflow: 'visible' }}>
        <h3 style={{ marginTop: 0, marginBottom: '2rem' }}>Event Timeline</h3>
        
        <div style={{ position: 'relative', minHeight: '100px' }}>
          {/* Timeline line */}
          <div style={{ 
            position: 'absolute', 
            left: '20px', 
            top: 0, 
            height: '100%',
            width: '2px', 
            background: 'var(--border-light)' 
          }} />
          
          {/* Timeline events */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {filteredEvents.map((event, index) => (
              <EventCard 
                key={event.id || index}
                event={event}
                previousEvent={index > 0 ? filteredEvents[index - 1] : null}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Event card component for timeline
function EventCard({ event, previousEvent }) {
  const [expanded, setExpanded] = useState(false);
  
  let payload = {};
  try {
    payload = JSON.parse(event.payloadJson || '{}');
  } catch (err) {
    console.error('Failed to parse payload:', err);
  }
  
  const eventTime = new Date(event.occurredAt);
  const timeAgo = previousEvent 
    ? formatDuration(eventTime - new Date(previousEvent.occurredAt))
    : '0s';
  
  const eventColor = EVENT_TYPE_COLORS[event.eventType] || '#6c757d';
  
  return (
    <div style={{ display: 'flex', gap: '1rem', position: 'relative' }}>
      {/* Timeline dot */}
      <div style={{ 
        position: 'relative',
        minWidth: '40px',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <div style={{ 
          width: '12px', 
          height: '12px', 
          borderRadius: '50%', 
          background: eventColor,
          border: '3px solid var(--card-bg)',
          boxShadow: `0 0 0 2px ${eventColor}`,
          position: 'relative',
          zIndex: 1
        }} />
      </div>
      
      {/* Event card */}
      <div style={{ 
        flex: 1, 
        background: 'var(--surface-secondary)', 
        padding: '1rem', 
        borderRadius: '8px',
        border: `1px solid ${eventColor}20`,
        borderLeft: `4px solid ${eventColor}`
      }}>
        {/* Event header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span style={{ 
                display: 'inline-block',
                padding: '0.25rem 0.5rem', 
                background: eventColor, 
                color: '#fff', 
                borderRadius: '4px', 
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                {event.eventType}
              </span>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                +{timeAgo}
              </span>
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {payload.label || payload.action || event.eventType}
            </div>
          </div>
          
          <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <div>{eventTime.toLocaleTimeString()}</div>
            <div>{eventTime.toLocaleDateString()}</div>
          </div>
        </div>
        
        {/* Event details */}
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', fontSize: '0.85rem', marginTop: '0.75rem' }}>
          {payload.entityType && (
            <>
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Entity:</span>
              <span>{payload.entityType}</span>
            </>
          )}
          {payload.entityId && (
            <>
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Entity ID:</span>
              <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{payload.entityId}</span>
            </>
          )}
          {payload.category && (
            <>
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Category:</span>
              <span>{payload.category}</span>
            </>
          )}
          {payload.action && (
            <>
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Action:</span>
              <span>{payload.action}</span>
            </>
          )}
        </div>
        
        {/* Metadata (collapsible) */}
        {payload.metadata && Object.keys(payload.metadata).length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <button 
              onClick={() => setExpanded(!expanded)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'var(--primary)', 
                cursor: 'pointer', 
                fontSize: '0.85rem',
                padding: 0,
                textDecoration: 'underline'
              }}
            >
              {expanded ? '▼ Hide' : '▶ Show'} Metadata
            </button>
            
            {expanded && (
              <div style={{ 
                marginTop: '0.5rem', 
                padding: '0.75rem', 
                background: 'var(--card-bg)', 
                borderRadius: '4px',
                fontSize: '0.8rem',
                fontFamily: 'monospace'
              }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(payload.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Simple metric card component
function MetricCard({ label, value }) {
  return (
    <div style={{ 
      background: 'var(--card-bg)', 
      padding: '1rem', 
      borderRadius: '8px', 
      border: '1px solid var(--border-light)',
      boxShadow: 'var(--shadow-sm)'
    }}>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

// Format duration in milliseconds to human-readable string
function formatDuration(ms) {
  if (ms < 0) return '0s';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}
